import { useEffect, useState } from 'react';
import { lerFragmento } from '../acesso/fragmento';
import { api, type AcessoEstado } from '../api';
import { useSession } from '../state/SessionContext';
import { CenaDeEntrada } from './entrada/CenaDeEntrada';
import { LandingAberta } from './landing/LandingAberta';
import { LandingFase } from './landing/LandingFase';

const ESPERA_MS = 4_000;

/**
 * Quando o estado do acesso não veio (servidor fora, rede): a landing da fase, que na dúvida não oferece cadastro
 * (fail secure; o servidor recusa de qualquer jeito). Entrar e Convite continuam no cartão.
 */
const ESTADO_NA_DUVIDA: AcessoEstado = { inscricoesAbertas: false, restrito: true, fase: null, proxima: null, antiRobo: null };
const DE_NOVO_MS = 30_000;

type Leitura =
  | { readonly status: 'carregando' }
  | { readonly status: 'ok'; readonly estado: AcessoEstado }
  | { readonly status: 'falhou' };

/** Lê o estado do acesso ao abrir, quando a aba volta a ficar visível e, se falhou, de novo em 30 s. */
function useEstadoAcesso(): Leitura {
  const [leitura, setLeitura] = useState<Leitura>({ status: 'carregando' });

  useEffect(() => {
    let vivo = true;
    let deNovo = 0;
    const ler = () => {
      window.clearTimeout(deNovo);
      const limite = new Promise<never>((_, falha) => window.setTimeout(() => falha(new Error('estado')), ESPERA_MS));
      void Promise.race([api.estadoAcesso(), limite])
        .then(estado => { if (vivo) setLeitura({ status: 'ok', estado }); })
        .catch(() => {
          if (!vivo) return;
          setLeitura(atual => (atual.status === 'ok' ? atual : { status: 'falhou' }));
          deNovo = window.setTimeout(ler, DE_NOVO_MS);
        });
    };
    const aoVoltar = () => { if (document.visibilityState === 'visible') ler(); };
    ler();
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      vivo = false;
      window.clearTimeout(deNovo);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, []);

  return leitura;
}

/**
 * `/entrar` saiu (Renato, 24/10/2026): a home tem o jogo inteiro e o login. Os e-mails já enviados ainda trazem
 * `/entrar#…`; o endereço vira `/` com o mesmo fragmento (e `#login` quando não há nenhum), antes de qualquer leitura.
 */
function migrarRotaAntiga(): void {
  if ((window.location.pathname.replace(/\/+$/, '') || '/') !== '/entrar') return;
  window.history.replaceState(null, '', '/' + window.location.search + (window.location.hash || '#login'));
}
migrarRotaAntiga();

/**
 * O e-mail do acesso manual traz `/#e=<e-mail>` (techspec-acesso-antecipado §21): o campo já vem preenchido. Fica no
 * fragmento (não vai para log) e sai da barra na primeira leitura; a resposta fica guardada aqui porque o React pode
 * desenhar o componente mais de uma vez. `#login` e `#criar` não são pares e ficam na barra (a landing rola até o cartão).
 */
let fragmentoLido: Record<string, string> | null = null;
function fragmentoDoLink(): Record<string, string> {
  if (fragmentoLido === null) fragmentoLido = window.location.hash.includes('=') ? lerFragmento() : {};
  return fragmentoLido;
}

/** O botão do e-mail de confirmação traz `/#confirmar=1&e=<e-mail>&c=<código>` (techspec-confirmacao-email). */
function confirmacaoDoLink(frag: Record<string, string>): { email: string; codigo: string } | undefined {
  const codigo = (frag.c ?? '').trim();
  if (frag.confirmar !== '1' || !frag.e || !/^\d{6}$/.test(codigo)) return undefined;
  return { email: frag.e, codigo };
}

/** O botão do e-mail de "esqueci a senha" traz `/#senha=1&e=<e-mail>&c=<código>` (techspec-esqueci-senha). */
function senhaDoLink(frag: Record<string, string>): { email: string; codigo: string } | undefined {
  const codigo = (frag.c ?? '').trim();
  if (frag.senha !== '1' || !frag.e || !/^\d{6}$/.test(codigo)) return undefined;
  return { email: frag.e, codigo };
}

/**
 * O link do e-mail do convite (`/convite#c=<código>&e=<e-mail>`) chega como `/#convite=1&c=…&e=…`
 * (AcessoPublico): a landing abre o login na aba Convite com os dois preenchidos.
 */
function conviteDoLink(frag: Record<string, string>): { codigo: string; email: string } | undefined {
  if (frag.convite !== '1') return undefined;
  return { codigo: (frag.c ?? '').trim(), email: (frag.e ?? '').trim() };
}

/**
 * O que `/` mostra para quem não está logado (techspec-landing-fase §05.2 e §06.7, techspec-landing-aberta §02):
 * - sem o modo restrito, a landing aberta, com o login no cartão (o link do e-mail preenche o cartão);
 * - com o restrito, a landing da fase; `#login`, `#convite` e os links de e-mail abrem o login no próprio cartão do
 *   herói, com Entrar e Convite (sem Criar conta) — uma página só, com o jogo e o acesso (Renato, 25/10/2026);
 * - estado que não veio: a landing da fase com {@link ESTADO_NA_DUVIDA}, sem cadastro (fail secure);
 * - "sem acesso a esta fase" na landing aberta (o modo mudou no meio): a landing da fase, onde dá para usar o convite.
 * A tela de login antiga (LoginScreen) saiu (Renato, 25/10/2026): o login é só o cartão da página principal.
 * Na landing aberta, o erro de entrar ou criar conta aparece no próprio cartão: a página fica.
 */
export function PortaDeEntrada() {
  const { verificando, errorCode } = useSession();
  const leitura = useEstadoAcesso();
  const frag = fragmentoDoLink();
  const confirmar = confirmacaoDoLink(frag);
  const senhaLink = senhaDoLink(frag);
  const convite = conviteDoLink(frag);
  const emailInicial = confirmar || senhaLink || convite ? '' : frag.e ?? '';

  // O erro de sessão (a volta do login) aparece no cartão da landing quando o estado chegar (no máximo 4 s).
  if (verificando || leitura.status === 'carregando') {
    return <div className="tela"><CenaDeEntrada /></div>;
  }
  const estado = leitura.status === 'ok' ? leitura.estado : null;
  // Na landing da fase o erro de entrar (inclusive "sem acesso a esta fase") aparece no cartão de login.
  if (!estado || estado.restrito || errorCode === 'acesso.semAcesso') {
    const daFase = estado ? { ...estado, restrito: true } : ESTADO_NA_DUVIDA;
    return <LandingFase estado={daFase} emailInicial={emailInicial} confirmar={confirmar} senhaLink={senhaLink} convite={convite} />;
  }
  return (
    <LandingAberta estado={estado} emailInicial={convite?.email || emailInicial} confirmar={confirmar} senhaLink={senhaLink} />
  );
}
